import { btn, h, icon } from '../ui/icons';
import { templeWords } from './EngraveBand';
import type { EngraveSession } from './EngraveSession';
import { FontDrawer } from './FontDrawer';
import { ew, FaceAccordion, faceSwitch, recapList, resetButton, symbolPalette, undoButton, type Part, type TextField } from './parts';
import { deleteLast, isEditable, slotFace, slotOf, slotSide, type EditableSlot, type SlotKey, type TempleSide } from './state';
import { TempleCard } from './TempleCard';

/**
 * A110Z — mobile « Gravure » screen (design handoff), opened from the 4th tab of Couleurs. Full
 * screen over the camera in the same glass as the other menus: header « ‹ Essayage · GRAVURE ·
 * ↺ Original », a card with the two temples and Intérieur / Extérieur, a card for the selected
 * slot (name, counter, colour, field, ✦), the font card (filter chips, two scrolling rows), the
 * summary, and « Valider la gravure ».
 *
 * Keyboard open: the font and summary cards hide; the temples and the field stay just above the
 * keyboard (visualViewport). ✦ swaps the keyboard for the symbol palette (ABC brings it back).
 *
 * A201 (David, 8 octobre) : the slot card becomes four panels, one per temple and face (`FaceAccordion`), only
 * one open; « Annuler » in the header; a loupe on each temple (the text enlarged, to place it with the finger);
 * the first time, a short demonstration of the drag on the temple.
 */
export interface EngraveScreenOptions {
  stage: HTMLElement;
  onClose: () => void;
}

const KEYBOARD_MIN_PX = 120;
/** The demonstration of the drag is shown once (this browser), for this long. */
const DEMO_KEY = 'mt-eng-demo-a201';
const DEMO_MS = 5000;
const LOUPE_ZOOM = 2.4;
const LOUPE_RATIO = 2.6;
let demoShown = false;

export class EngraveScreen {
  readonly root = h('div', 'mt-eng-screen');
  private readonly scroll = h('div', 'mt-eng-scroll');
  private readonly cards: TempleCard[];
  private readonly cardGlass: HTMLElement[] = [];
  private readonly accordion: FaceAccordion;
  private readonly loupe = h('div', 'mt-eng-loupe');
  private readonly loupeTitle = h('span', 'mt-eng-card-title');
  private readonly loupeCards: TempleCard[];
  private loupeSide: TempleSide | null = null;
  private readonly demo = h('div', 'mt-eng-demo');
  /** A201: the temple being edited, kept in view at the top while the panels scroll (its own drag too). */
  private readonly mini = h('div', 'mt-eng-mini');
  private readonly miniCards: TempleCard[];
  private miniWanted = false;
  private observer: IntersectionObserver | null = null;
  private demoTimer = 0;
  private symField: TextField | null = null;
  private readonly fontDrawer: FontDrawer;
  private readonly recapCard = h('section', 'mt-eng-card mt-eng-card--recap');
  private readonly foot = h('div', 'mt-eng-foot');
  private readonly kbPanel = h('div', 'mt-eng-kbpanel');
  private readonly palette = symbolPalette(g => this.insertSymbol(g));
  private readonly parts: Part[] = [];
  private readonly temples = h('section', 'mt-eng-card mt-eng-card--temples');
  private symMode = false;
  private open_ = false;
  private unsubscribe: (() => void) | null = null;
  private readonly vv = typeof window !== 'undefined' ? window.visualViewport : null;

  constructor(private readonly session: EngraveSession, private readonly opts: EngraveScreenOptions) {
    const w = ew();
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', w.title);

    // Header
    const top = h('header', 'mt-eng-top');
    const back = btn('mt-eng-back');
    back.append(h('span', 'mt-eng-chevron', '‹'), h('span', '', w.back));
    back.setAttribute('aria-label', w.back);
    back.onclick = () => this.close();
    const title = h('h2', 'mt-eng-title', w.title);
    const reset = resetButton(session, 'mt-eng-reset mt-eng-reset--top');
    const undo = undoButton(session, 'mt-eng-undo mt-eng-undo--top', false);
    const actions = h('div', 'mt-eng-top-actions');
    actions.append(undo.root, reset);
    top.append(back, title, actions);
    this.parts.push(undo);

    // Temples card
    const tHead = h('div', 'mt-eng-card-head');
    const tTitle = h('span', 'mt-eng-card-title');
    const faces = faceSwitch(session, 'mt-eng-faces--card');
    tHead.append(tTitle, faces.root);
    this.temples.append(tHead);
    this.cards = (['l', 'r'] as TempleSide[]).map(side => {
      const card = new TempleCard({
        side,
        words: templeWords(true),
        onSelect: slot => session.setActive(slot),
        onActivate: slot => this.selectSlot(slot),
        onPlace: (slot, place) => session.setPlace(slot, place, { clamp: true }),
        ratio: 7.5,
      });
      const glass = h('div', 'mt-eng-card-inner');
      const zoom = btn('mt-eng-zoom');
      zoom.append(icon('zoom'));
      zoom.setAttribute('aria-label', `${w.zoom} · ${w.temple(side)}`);
      zoom.dataset.side = side;
      zoom.onclick = () => this.openLoupe(side);
      // In the card's head row (beside « Intérieur »), never over the drawing where the text is dragged.
      card.root.querySelector('.mt-temple-head')?.append(zoom);
      glass.append(card.root);
      this.cardGlass.push(glass);
      this.temples.append(glass);
      return card;
    });
    const miniGlass = h('div', 'mt-eng-card-inner mt-eng-mini-card');
    this.miniCards = (['l', 'r'] as TempleSide[]).map(side => {
      const card = new TempleCard({
        side,
        words: templeWords(true),
        onSelect: slot => session.setActive(slot),
        onActivate: slot => this.selectSlot(slot),
        onPlace: (slot, place) => session.setPlace(slot, place, { clamp: true }),
        ratio: 7.5,
      });
      miniGlass.append(card.root);
      return card;
    });
    this.mini.append(miniGlass);
    this.mini.hidden = true;
    this.demo.append(h('span', 'mt-eng-demo-finger'), h('span', 'mt-eng-demo-text', w.demo));
    this.demo.setAttribute('aria-hidden', 'true');
    this.demo.hidden = true;
    this.parts.push(faces, { root: tTitle, refresh: () => { tTitle.textContent = w.yourTemples(session.frame.model); } });

    // A201: the four views, one panel each
    const facesCard = h('section', 'mt-eng-card mt-eng-card--faces');
    this.accordion = new FaceAccordion(session, {
      mobile: true,
      onSymbols: f => this.toggleSymbols(f),
      onFocus: () => this.leaveSymbols(false),
    });
    facesCard.append(this.accordion.root);
    this.parts.push(this.accordion);

    this.fontDrawer = new FontDrawer(session);
    this.parts.push({ root: this.fontDrawer.trigger, refresh: () => this.fontDrawer.refresh() });

    // Summary
    const rHead = h('div', 'mt-eng-card-head');
    rHead.append(h('span', 'mt-eng-card-title', w.recap));
    const recap = recapList(session);
    this.recapCard.append(rHead, recap.root);
    this.parts.push(recap);

    // Footer
    const validate = btn('mt-cta mt-eng-validate', w.validate);
    validate.onclick = () => this.close();
    this.foot.append(this.fontDrawer.trigger, validate);

    // A201 loupe: one temple enlarged around its text, over the screen
    const lHead = h('div', 'mt-eng-card-head');
    const lClose = btn('mt-eng-loupe-close');
    lClose.append(icon('close'));
    lClose.setAttribute('aria-label', w.zoomClose);
    lClose.onclick = () => this.closeLoupe();
    lHead.append(this.loupeTitle, lClose);
    const lGlass = h('div', 'mt-eng-card-inner mt-eng-loupe-card');
    this.loupeCards = (['l', 'r'] as TempleSide[]).map(side => {
      const card = new TempleCard({
        side,
        words: templeWords(true),
        onSelect: slot => session.setActive(slot),
        onPlace: (slot, place) => session.setPlace(slot, place, { clamp: true }),
        ratio: LOUPE_RATIO,
        zoom: LOUPE_ZOOM,
      });
      lGlass.append(card.root);
      return card;
    });
    const lDone = btn('mt-cta mt-eng-loupe-done', w.ok);
    lDone.onclick = () => this.closeLoupe();
    this.loupe.append(lHead, lGlass, h('p', 'mt-eng-note mt-eng-loupe-hint', w.zoomHint), lDone);
    this.loupe.hidden = true;
    this.loupe.setAttribute('role', 'dialog');
    this.loupe.setAttribute('aria-label', w.zoom);

    // Symbol panel (replaces the keyboard)
    const bar = h('div', 'mt-eng-kbbar');
    const abc = btn('mt-eng-key mt-eng-key--abc', w.abc);
    abc.onclick = () => this.leaveSymbols(true);
    const del = btn('mt-eng-key mt-eng-key--del'); del.setAttribute('aria-label', w.erase); del.append(deleteIcon());
    del.onclick = () => { const s = this.symField?.slot ?? this.editableActive(); if (s) session.setText(s, deleteLast(session.state.texts[s])); };
    const ok = btn('mt-eng-key mt-eng-key--ok', w.ok);
    ok.onclick = () => { this.leaveSymbols(false); session.setActive(session.active); this.root.dataset.kb = ''; };
    bar.append(abc, del, ok);
    this.kbPanel.append(this.palette.root, bar);
    this.kbPanel.hidden = true;

    this.temples.append(this.demo);
    this.scroll.append(this.mini, this.temples, facesCard, this.recapCard);
    // The mini temple shows once the temples card has mostly scrolled out of view.
    if (typeof IntersectionObserver !== 'undefined') {
      this.observer = new IntersectionObserver(entries => {
        const e = entries[entries.length - 1];
        if (!e) return;
        this.miniWanted = e.intersectionRatio < 0.3;
        this.refreshMini();
      }, { root: this.scroll, threshold: [0, 0.3, 0.6] });
    }
    this.root.append(top, this.accordion.tabs, this.scroll, this.foot, this.kbPanel, this.loupe);
    this.root.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (!this.loupe.hidden) this.closeLoupe(); else this.close();
    });
    // Blur / focus of a field → keyboard state
    for (const field of this.accordion.fields.values()) {
      field.input.addEventListener('focus', () => this.updateKeyboard());
      field.input.addEventListener('blur', () => window.setTimeout(() => this.updateKeyboard(), 50));
    }
    // The demonstration goes away at the first touch.
    this.root.addEventListener('pointerdown', () => this.hideDemo(), { capture: true });
  }

  get isOpen(): boolean { return this.open_; }

  open(): void {
    if (this.open_) return;
    this.open_ = true;
    this.session.prepareFonts();
    if (!this.session.active) this.session.setActive('ir');
    this.root.hidden = false;
    this.opts.stage.classList.add('mt-stage--engrave');
    if (!this.root.isConnected) this.opts.stage.append(this.root);
    this.unsubscribe = this.session.onChange(event => {
      if (event === 'face') this.followFace();
      this.refresh();
    });
    this.vv?.addEventListener('resize', this.onViewport);
    this.vv?.addEventListener('scroll', this.onViewport);
    this.onViewport();
    this.refresh();
    this.scroll.scrollTop = 0;
    this.observer?.observe(this.temples);
    this.maybeDemo();
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;
    this.fontDrawer.close();
    this.observer?.disconnect();
    this.miniWanted = false;
    this.mini.hidden = true;
    this.leaveSymbols(false);
    this.closeLoupe();
    this.hideDemo();
    this.accordion.activeField()?.input.blur();
    this.root.hidden = true;
    this.opts.stage.classList.remove('mt-stage--engrave');
    this.root.dataset.kb = '';
    this.unsubscribe?.(); this.unsubscribe = null;
    this.vv?.removeEventListener('resize', this.onViewport);
    this.vv?.removeEventListener('scroll', this.onViewport);
    this.root.style.height = ''; this.root.style.transform = '';
    this.opts.onClose();
  }

  destroy(): void {
    this.close();
    this.fontDrawer.destroy();
    this.root.remove();
  }

  // ------------------------------------------------------------ slots

  private editableActive(): EditableSlot | null {
    const a = this.session.active;
    return a && isEditable(a) ? a : null;
  }

  /** A zone tapped: opens its panel; a text opens the keyboard. */
  private selectSlot(slot: SlotKey): void {
    this.session.setActive(slot);
    this.refresh();
    if (isEditable(slot)) {
      if (!this.symMode) this.accordion.fields.get(slot)?.focus();
    } else {
      this.leaveSymbols(false);
      (document.activeElement as HTMLElement | null)?.blur?.();
    }
  }

  // ------------------------------------------------------------ loupe (A201)

  private openLoupe(side: TempleSide): void {
    this.leaveSymbols(false);
    this.accordion.activeField()?.input.blur();
    this.session.setActive(slotOf(side, this.session.face));
    this.loupeSide = side;
    this.loupe.hidden = false;
    this.root.dataset.loupe = '1';
    this.refresh();
    this.loupeCards.forEach(c => c.recentre());
  }

  private closeLoupe(): void {
    if (this.loupe.hidden) return;
    this.loupe.hidden = true;
    this.loupeSide = null;
    this.root.dataset.loupe = '';
  }

  // ------------------------------------------------------------ first-use demonstration (A201)

  private maybeDemo(): void {
    if (demoShown) return;
    let seen = false;
    try { seen = window.localStorage.getItem(DEMO_KEY) === '1'; } catch { /* storage blocked */ }
    if (seen || new URLSearchParams(window.location.search).has('nodemo')) { demoShown = true; return; }
    demoShown = true;
    try { window.localStorage.setItem(DEMO_KEY, '1'); } catch { /* storage blocked */ }
    // Over the temple being edited (inside right by default).
    const side = slotSide(this.session.active ?? 'ir');
    const glass = this.cardGlass[side === 'l' ? 0 : 1];
    if (!glass) return;
    glass.append(this.demo);
    this.demo.hidden = false;
    // Counted from the first frame on screen (a busy first frame must not eat the demonstration).
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!this.demo.hidden) this.demoTimer = window.setTimeout(() => this.hideDemo(), DEMO_MS);
    }));
  }

  private hideDemo(): void {
    window.clearTimeout(this.demoTimer);
    this.demo.hidden = true;
  }

  /** Face switched: keep the same side on the new face. */
  private followFace(): void {
    const a = this.session.active;
    if (a && slotFace(a) !== this.session.face) this.session.setActive(slotOf(slotSide(a), this.session.face));
  }

  // ------------------------------------------------------------ symbols / keyboard

  private toggleSymbols(field: TextField): void {
    if (this.symMode && this.symField === field) { this.leaveSymbols(true); return; }
    if (this.symMode) this.leaveSymbols(false);
    this.symMode = true;
    this.symField = field;
    if (field.slot) this.session.setActive(field.slot);
    this.root.dataset.sym = '1';
    this.kbPanel.hidden = false;
    field.symbols.setAttribute('aria-expanded', 'true');
    // The keyboard goes away; the field stays the active slot.
    field.input.blur();
    this.updateKeyboard();
  }

  private leaveSymbols(backToKeyboard: boolean): void {
    if (!this.symMode) return;
    const field = this.symField;
    this.symMode = false;
    this.symField = null;
    this.root.dataset.sym = '';
    this.kbPanel.hidden = true;
    field?.symbols.setAttribute('aria-expanded', 'false');
    if (backToKeyboard) field?.focus();
    this.updateKeyboard();
  }

  private insertSymbol(glyph: string): void {
    this.symField?.insert(glyph);
  }

  private readonly onViewport = (): void => {
    if (!this.open_) return;
    const vv = this.vv;
    if (vv) {
      const stage = this.opts.stage.getBoundingClientRect();
      // Height of the visible part of the stage; the screen follows the visual viewport.
      const visibleBottom = Math.min(stage.bottom, vv.offsetTop + vv.height);
      const height = Math.max(200, Math.floor(visibleBottom - stage.top));
      this.root.style.height = `${Math.round(height)}px`;
      this.root.style.transform = vv.offsetTop > 0 && stage.top < 0 ? `translateY(${Math.round(vv.offsetTop)}px)` : '';
    }
    this.updateKeyboard();
  };

  private updateKeyboard(): void {
    const focused = [...this.accordion.fields.values()].some(f => document.activeElement === f.input);
    const shrunk = this.vv ? window.innerHeight - this.vv.height > KEYBOARD_MIN_PX : false;
    const kb = (focused || shrunk) && !this.symMode;
    this.root.dataset.kb = kb || this.symMode ? '1' : '';
    this.refreshMini();
    if (kb) requestAnimationFrame(() => { this.scroll.scrollTop = this.scroll.scrollHeight; });
  }

  // ------------------------------------------------------------ render

  refresh(): void {
    if (!this.open_) return;
    const s = this.session;
    for (const p of this.parts) p.refresh();
    const view = { templeId: s.frame.id, face: s.face, image: s.image, state: s.state, active: s.active };
    for (const [i, card] of this.cards.entries()) {
      this.cardGlass[i]!.hidden = Boolean(s.active && slotSide(s.active) !== (i === 0 ? 'l' : 'r'));
      card.update(view);
      this.cardGlass[i]!.classList.toggle('mt-eng-card-inner--active', Boolean(card.slot && card.slot === s.active));
    }
    if (this.loupeSide) {
      const w = ew();
      this.loupeCards.forEach((card, i) => {
        const on = (i === 0 ? 'l' : 'r') === this.loupeSide;
        card.root.hidden = !on;
        if (on) card.update(view);
      });
      const slot = slotOf(this.loupeSide, s.face);
      this.loupeTitle.textContent = `${w.zoom} · ${w.slots[slot]}`;
    }
    const active = s.active;
    this.root.dataset.locked = active && !isEditable(active) ? '1' : '';
    this.refreshMini();
  }

  /** The mini temple: the side being edited, while the temples card is out of view (not with the keyboard up). */
  private refreshMini(): void {
    const s = this.session;
    const show = this.open_ && this.miniWanted && Boolean(s.active) && this.root.dataset.kb !== '1' && this.loupe.hidden;
    this.mini.hidden = !show;
    if (!show || !s.active) return;
    const side = slotSide(s.active);
    const view = { templeId: s.frame.id, face: s.face, image: s.image, state: s.state, active: s.active };
    this.miniCards.forEach((card, i) => {
      const on = (i === 0 ? 'l' : 'r') === side;
      card.root.hidden = !on;
      if (on) card.update(view);
    });
  }
}

function deleteIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '22'); svg.setAttribute('height', '22');
  svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.6');
  svg.setAttribute('stroke-linecap', 'round'); svg.setAttribute('stroke-linejoin', 'round'); svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = '<path d="M8.5 6h11A1.5 1.5 0 0 1 21 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-11L3 12z"/><path d="M11 9.5l5 5M16 9.5l-5 5"/>';
  svg.classList.add('mt-icon');
  return svg;
}

